import React, {useState} from "react";
import {Trans, useTranslation} from "react-i18next";
import {Button, Divider, Modal, Spin, Typography} from "antd";
import {capitalize} from "lodash";
import {
  AssetsFiltersFE,
  AssetsOptionLabelValueFilters,
  fromFeToBeAssetsFilters
} from "../../../../types/internal/tables/assetsTable";
import {useGetAssetMapQuery, usePrefetch} from "../../../../redux/api/assetsMap";
import {skipToken} from "@reduxjs/toolkit/query";
import {useEventContext} from "../../../../context/event/EventContext";
import {SelectCluster} from "../../importAlertsAreas/AssignCluster";
import {CHANGES} from "../../../../types/api/getEventsResult";

function getAssetsMapQueryParams(filters: AssetsFiltersFE, filtersInExcludeMode: AssetsOptionLabelValueFilters[], eventUuid: string) {
  return {
    filters: {
      ...fromFeToBeAssetsFilters(filters, filtersInExcludeMode),
      event_uuid: eventUuid
    }
  }
}

interface AssignAssetsToClusterModalProps {
  isVisible: boolean,
  onClose: () => void,
  filters: AssetsFiltersFE,
  filtersInExcludeMode: AssetsOptionLabelValueFilters[],
  callbackUpdateTable: () => void,
  eventUuid: string
}
function AssignAssetsToClusterModal({eventUuid, isVisible, onClose, filters, filtersInExcludeMode, callbackUpdateTable}: AssignAssetsToClusterModalProps) {
  const {t} = useTranslation();
  const {
    data: filteredAssets,
    isFetching: isFetchingFilteredAssets,
    isLoading: isLoadingFilteredAssets,
  } = useGetAssetMapQuery(isVisible ? getAssetsMapQueryParams(filters, filtersInExcludeMode, eventUuid) : skipToken);
  const {clusters, updateClusterAssets} = useEventContext()
  const [selectedCluster, setSelectedCluster] = useState<string>("");
  const [isUpdatingAssets, setIsUpdatingAssets] = useState(false);
const {setPolygonsAndAssetsForTheMap} = useEventContext()

  function handleCancel() {
    onClose();
  }

  return (
    <Modal
      title={capitalize(t('clusters.addToClusterModalTitle'))}
      open={isVisible}
      onCancel={handleCancel}
      closeIcon={true}
      footer={[
        <Button key="back" onClick={handleCancel}>
          {capitalize(t("actions.close"))}
        </Button>,
        <Button key="submit" type="primary" disabled={!filteredAssets?.length} onClick={async () => {
          setIsUpdatingAssets(true);

          const assets = filteredAssets?.map(el => el.uuid) || [];

          try {
            await updateClusterAssets({
              cluster: selectedCluster,
              assets,
              note: null,
            });
            callbackUpdateTable()
            await setPolygonsAndAssetsForTheMap(CHANGES.WITH)
          } catch (e) {
            console.error(e);
          } finally {
            setIsUpdatingAssets(false);
            onClose();
          }
        }}>
          {capitalize(t("actions.confirm"))}
        </Button>,
      ]}
    >
      <Spin spinning={isLoadingFilteredAssets || isFetchingFilteredAssets || isUpdatingAssets}>
        <Divider />
        <div style={{display: 'flex', flexDirection: 'column', gap: '1.5rem'}}>
          <Trans
            i18nKey={'clusters.addToClusterModalDisclaimer'}
            components={{p: <Typography.Paragraph style={{margin: 0}}/>}}
            values={{
              number: filteredAssets?.length || 0,
            }}
          />
          <SelectCluster
            clusters={clusters}
            nullValue={true}
            select={{
              value: selectedCluster,
              onSelect: (v) => {
                setSelectedCluster(v);
              }
            }}
          />
          <Trans
            i18nKey={'clusters.addToClusterModalRecap'}
            components={{p: <Typography.Paragraph/>}}
            values={{
              number: filteredAssets?.length || 0,
            }}
          />
        </div>
        <Divider />
      </Spin>
    </Modal>
  );
}

interface AssignAssetsToClusterButtonProps {
  filters: AssetsFiltersFE,
  filtersInExcludeMode: AssetsOptionLabelValueFilters[],
  callbackUpdateTable: () => void,
  eventUuid: string
}
export default function AssignAssetsToClusterButton({eventUuid, filters, filtersInExcludeMode, callbackUpdateTable}: AssignAssetsToClusterButtonProps) {
  const {t} = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const prefetchFilteredAssets = usePrefetch("getAssetMap");

  return (
    <>
      <Button
        type={"primary"}
        onMouseEnter={() => prefetchFilteredAssets(getAssetsMapQueryParams(filters, filtersInExcludeMode, eventUuid))}
        onClick={() => setIsModalVisible(true)}
      >
        {capitalize(t('clusters.addToCluster'))}
      </Button>
      <AssignAssetsToClusterModal
        isVisible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        filters={filters}
        filtersInExcludeMode={filtersInExcludeMode}
        callbackUpdateTable={callbackUpdateTable}
        eventUuid={eventUuid}
      />
    </>
  );
}